import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Sliders, X } from "lucide-react";

// Two-stage filter surface (Feb 2026 revision):
//
//   1. COMPACT PROMPT (default) — a slim inline pill asking "Want to narrow
//      your options?" with two low-key choices: "Yes, show filters" or
//      "No — show me everyone". Keeps the chat visually calm.
//
//   2. EXPANDED PANEL — appears only after the family opts in from stage 1
//      (or reopens it later via the persistent "Add filters" link). Contains
//      the always-open Insurance / Programs / Services sections plus an
//      "Other" free-text input per section.
//
// If the family opts out at stage 1, we collapse to a single tiny link that
// says "🔍 Add filters" so they can always come back later without the panel
// hogging the screen.

const INSURANCE_OPTIONS = [
  "Medicare",
  "Medicaid",
  "Long-Term Care Insurance",
  "Private Pay",
  "Veterans Benefits (VA)",
  "Aetna",
  "Blue Cross Blue Shield",
  "UnitedHealthcare",
  "Humana",
  "Cigna",
];

const PROGRAM_OPTIONS = [
  "Dementia / Alzheimer's Care",
  "Parkinson's Care",
  "Post-Surgery Recovery",
  "Palliative / End-of-Life",
  "Diabetes Management",
  "Fall Prevention",
  "Veterans-Focused",
  "Cardiac Care",
  "Stroke Recovery",
];

const SERVICE_OPTIONS = [
  "Bathing & Hygiene",
  "Medication Management",
  "Meal Preparation",
  "Companionship",
  "Transportation",
  "Housekeeping",
  "Skilled Nursing",
  "Physical / Occupational Therapy",
  "24/7 Care",
  "Overnight Care",
];

function FilterSection({ title, options, selected, onToggle, otherValue, onOtherChange, otherPlaceholder, testIdPrefix }) {
  const count = selected.length + (otherValue.trim() ? 1 : 0);
  return (
    <div className="border border-border rounded-xl bg-white/70 overflow-hidden">
      <div className="w-full flex items-center justify-between px-4 pt-3 pb-2" data-testid={`${testIdPrefix}-section`}>
        <span className="font-body text-sm font-medium text-text-primary">
          {title}
          {count > 0 && (
            <span className="ml-2 inline-flex items-center justify-center rounded-full bg-primary/10 text-primary text-xs px-2 py-0.5">
              {count}
            </span>
          )}
        </span>
      </div>
      <div className="px-4 pb-3 pt-1 grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4">
        {options.map((opt) => {
          const id = `${testIdPrefix}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
          const checked = selected.includes(opt);
          return (
            <label key={opt} htmlFor={id} className="flex items-center gap-2 cursor-pointer text-sm text-text-primary py-1">
              <Checkbox id={id} checked={checked} onCheckedChange={() => onToggle(opt)} data-testid={id} />
              <span>{opt}</span>
            </label>
          );
        })}
      </div>
      <div className="px-4 pb-4 -mt-1">
        <label htmlFor={`${testIdPrefix}-other`} className="block text-xs font-body text-text-secondary mb-1.5">
          Other — type anything else
        </label>
        <Input
          id={`${testIdPrefix}-other`}
          type="text"
          value={otherValue}
          onChange={(e) => onOtherChange(e.target.value)}
          placeholder={otherPlaceholder}
          className="bg-white text-sm"
          data-testid={`${testIdPrefix}-other-input`}
        />
      </div>
    </div>
  );
}

export default function AsherFilterCard({ onSubmit, onSkip }) {
  // "prompt" = compact inline ask, "expanded" = full panel, "collapsed" = tiny link
  const [stage, setStage] = useState("prompt");
  const [insurance, setInsurance] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [services, setServices] = useState([]);
  const [insuranceOther, setInsuranceOther] = useState("");
  const [programsOther, setProgramsOther] = useState("");
  const [servicesOther, setServicesOther] = useState("");

  const toggle = (setter) => (value) =>
    setter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));

  const splitOther = (raw) => (raw || "").split(",").map((s) => s.trim()).filter(Boolean);

  const insuranceAll = [...insurance, ...splitOther(insuranceOther)];
  const programsAll = [...programs, ...splitOther(programsOther)];
  const servicesAll = [...services, ...splitOther(servicesOther)];
  const total = insuranceAll.length + programsAll.length + servicesAll.length;

  // STAGE 1 — compact prompt. Small, quiet, easy to say no to.
  if (stage === "prompt") {
    return (
      <div
        className="rounded-xl border border-primary/20 bg-white px-4 py-3 flex flex-wrap items-center gap-3"
        data-testid="asher-filter-prompt"
      >
        <Sliders className="w-4 h-4 text-primary shrink-0" />
        <p className="text-sm font-body text-text-primary flex-1 min-w-[200px]">
          Want to narrow your options — insurance, services, or specialty programs?
        </p>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              onSkip?.();
              setStage("collapsed");
            }}
            className="text-text-secondary hover:text-text-primary font-body"
            data-testid="filter-prompt-skip"
          >
            No need
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => setStage("expanded")}
            className="bg-primary text-white hover:bg-primary/90 font-body"
            data-testid="filter-prompt-open"
          >
            Yes, show filters
          </Button>
        </div>
      </div>
    );
  }

  // STAGE 3 — tiny persistent link so families can reopen filters anytime.
  if (stage === "collapsed") {
    return (
      <div className="flex justify-end" data-testid="asher-filter-collapsed">
        <button
          type="button"
          onClick={() => setStage("expanded")}
          className="inline-flex items-center gap-1.5 text-xs font-body text-text-secondary hover:text-primary transition-colors"
          data-testid="filter-reopen"
        >
          <Sliders className="w-3.5 h-3.5" />
          {total > 0 ? `Filters (${total})` : "Add filters"}
        </button>
      </div>
    );
  }

  // STAGE 2 — the full panel. Still less shouty than before (softer bg, tighter padding).
  return (
    <div className="rounded-2xl border border-primary/20 bg-white p-4 sm:p-5 space-y-4" data-testid="asher-filter-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <Sliders className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h3 className="font-heading text-base font-semibold text-text-primary">Narrow your options</h3>
            <p className="text-xs text-text-secondary font-body">Check any that matter to you.</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setStage("collapsed")}
          className="text-text-secondary hover:text-text-primary transition-colors"
          aria-label="Close filters"
          data-testid="filter-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-2">
        <FilterSection
          title="Insurance / Payment"
          options={INSURANCE_OPTIONS}
          selected={insurance}
          onToggle={toggle(setInsurance)}
          otherValue={insuranceOther}
          onOtherChange={setInsuranceOther}
          otherPlaceholder="e.g. Kaiser Permanente, workers comp"
          testIdPrefix="filter-insurance"
        />
        <FilterSection
          title="Specialty Programs"
          options={PROGRAM_OPTIONS}
          selected={programs}
          onToggle={toggle(setPrograms)}
          otherValue={programsOther}
          onOtherChange={setProgramsOther}
          otherPlaceholder="e.g. wound care, ALS, memory care"
          testIdPrefix="filter-program"
        />
        <FilterSection
          title="Services Needed"
          options={SERVICE_OPTIONS}
          selected={services}
          onToggle={toggle(setServices)}
          otherValue={servicesOther}
          onOtherChange={setServicesOther}
          otherPlaceholder="e.g. respite care, pet care, errands"
          testIdPrefix="filter-service"
        />
      </div>

      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-1">
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            onSkip?.();
            setStage("collapsed");
          }}
          className="text-text-secondary hover:text-text-primary"
          data-testid="filter-skip-btn"
        >
          Skip — show me everyone
        </Button>
        <Button
          type="button"
          onClick={() => {
            onSubmit({
              insurance_filters: insuranceAll,
              program_filters: programsAll,
              service_filters: servicesAll,
            });
            setStage("collapsed");
          }}
          className="bg-primary text-white hover:bg-primary/90"
          data-testid="filter-continue-btn"
        >
          {total > 0 ? `Apply ${total} filter${total === 1 ? "" : "s"}` : "Apply"}
        </Button>
      </div>
    </div>
  );
}
